import React, {useContext, useMemo} from 'react';
import {Box, Grid} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import {ContactActionsDropdown} from '../ContactActions/ContactActionsDropdown';
import {RenderContactNameAndEmail} from '../ContactPage/ContactSearch';
import {RenderColorBasic} from '../EditorGrid/GridRenderComponents';
import {SearchResults} from '../Common/SearchResults';
import {ContactSearchFragment} from '../../schema-types/ContactSearchFragment';
import {
  atrStatusCategoryColors,
  atrStatusCategoryText,
  clientPortalStatusCategoryColors,
  clientPortalStatusCategoryText,
  ecqStatusCategoryColors,
  ecqStatusCategoryText,
  fpaStatusCategoryColors,
  fpaStatusCategoryText,
} from '../AccountContactsGrid/singleAccountContactColumns';
import {
  AtrStatusCategory,
  ClientPortalStatusCategory,
  GlobalPermissionEnum,
  OneviewDocumentSignatureRequestStatusChoices,
} from '../../schema-types/globalTypes';
import {AccountContact} from '../../schema-types/AccountContact';
import {themeColors} from '../../theme/Theme';
import {OpenInCuro} from '../../utils/getCuroURL';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {PENDING_MESSAGE} from '../ContactActions/RequestFpaSignature';
import ContactActionsContext from '../ContactActions/ContactActionsContext';
import {newUTCDate} from '../../utils/newUTCDate';
import {renderPhone} from '../../utils/renderPhone';
import {renderEmail} from '../../utils/renderEmail';
import {sortAccountContacts} from './sortAccountContacts';
import {useAccountContext} from '../../contexts/AccountContext';
import {useRTQ} from '../../hooks/useRTQ';
import {AccountContactSlow} from '../../schema-types/AccountContactSlow';

type ContactColumnProps = GridColDef<AccountContact & AccountContactSlow> & {
  field: keyof ContactSearchFragment | 'primary' | string;
};

const useContactSaltusColumns = (): ContactColumnProps[] => {
  const {atrName} = useRTQ();
  return [
    {
      field: 'name',
      headerName: ' ',
      // width: mediumColumnWidth,
      flex: 2,
      renderCell: RenderContactNameAndEmail,
    },

    {
      field: 'atrSent',
      headerName: `${atrName} Date Sent`,
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const data = params.row;
        if (!data?.statuses?.atr?.status) {
          return RenderColorBasic({Loading: 'lightgrey'}, 'Loading', 'Loading');
        }
        if (!data?.statuses?.atr?.dateSent) {
          return RenderColorBasic(
            {'Not available': 'lightgrey'},
            'Not available',
            'Not available'
          );
        }
        return RenderColorBasic(
          atrStatusCategoryColors,
          'done',
          newUTCDate(data.statuses?.atr?.dateSent).toLocaleDateString()
        );
      },
    },
    {
      field: 'atrStatus',
      headerName: `${atrName} Last Updated`,
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const data = params.row;
        if (!data?.statuses?.atr?.status) {
          return RenderColorBasic({Loading: 'lightgrey'}, 'Loading', 'Loading');
        }

        const statusText =
          data.statuses?.atr.status === AtrStatusCategory.not_started
            ? atrStatusCategoryText.not_started
            : atrStatusCategoryText[data.statuses?.atr.status];
        const dateText = data.statuses?.atr.dateSubmitted
          ? newUTCDate(data.statuses?.atr.dateSubmitted).toLocaleDateString()
          : '';

        return (
          <>
            {RenderColorBasic(
              atrStatusCategoryColors,
              data.statuses?.atr.status,
              statusText,
              <div style={{marginTop: '4px'}}>{dateText}</div>
            )}
          </>
        );
      },
    },
    {
      field: 'fpaStatus',
      headerName: 'FPA Status',
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const data = params.row;

        if (!data?.fpa?.status) {
          return RenderColorBasic(
            {'Not available': 'lightgrey'},
            'Not available',
            'Not available'
          );
        }
        const dateText = newUTCDate(data.fpa?.dateSigned).toLocaleDateString();
        return (
          <>
            {RenderColorBasic(
              fpaStatusCategoryColors,
              data.fpa.status,
              fpaStatusCategoryText[data.fpa.status],
              data?.fpa?.status === OneviewDocumentSignatureRequestStatusChoices.SIGNED
                ? data.fpa.dateSigned && <div style={{marginTop: '4px'}}>{dateText}</div>
                : null,
              undefined,
              data?.fpa?.status === OneviewDocumentSignatureRequestStatusChoices.PENDING
                ? PENDING_MESSAGE
                : ''
            )}
          </>
        );
      },
    },
    {
      field: 'factFindStatus',
      headerName: 'Fact Find',
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const data = params.row;
        if (!data) {
          return RenderColorBasic({Loading: 'lightgrey'}, 'Loading', 'Loading');
        }
        const {ecqStatus} = data;
        return (
          <>
            {RenderColorBasic(
              ecqStatusCategoryColors,
              ecqStatus ?? '',
              ecqStatus ? ecqStatusCategoryText[ecqStatus] : 'Not started',
              data.statuses?.factFind?.lastSeen ? (
                <div style={{marginTop: '4px'}}>
                  {newUTCDate(data.statuses?.factFind?.lastSeen).toLocaleDateString()}
                </div>
              ) : null
            )}
          </>
        );
      },
    },
    {
      field: 'clientPortalStatus',
      headerName: 'Client Portal',
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const data = params.row;
        if (!data?.statuses?.clientPortal?.status) {
          return RenderColorBasic({Loading: 'lightgrey'}, 'Loading', 'Loading');
        }

        return (
          <>
            {RenderColorBasic(
              clientPortalStatusCategoryColors,
              data.statuses?.clientPortal.status,
              data.statuses?.clientPortal.status ===
                ClientPortalStatusCategory.not_started
                ? clientPortalStatusCategoryText.not_started
                : `${clientPortalStatusCategoryText[data.statuses.clientPortal.status]}`,
              data.statuses?.clientPortal.status ===
                ClientPortalStatusCategory.not_started ? null : (
                <div style={{marginTop: '4px'}}>
                  {newUTCDate(
                    data.statuses?.clientPortal.lastUpdated
                  ).toLocaleDateString()}
                </div>
              )
            )}
          </>
        );
      },
    },
    {
      field: 'primaryPhone',
      headerName: 'Phone',
      align: 'center',
      flex: 1,
      renderCell: renderPhone,
    },
    {
      field: 'homePhone',
      headerName: 'Home phone',
      align: 'center',
      flex: 1,
      hideSortIcons: true,
      renderCell: renderPhone,
    },
    {
      field: 'id',
      headerName: 'Actions',
      align: 'center',
      flex: 1,
      hideSortIcons: true,
      renderCell: (params) => {
        const data = params.row;
        if (!data) {
          return RenderColorBasic({Loading: 'lightgrey'}, 'Loading', 'Loading');
        }
        return (
          <Box display="flex" flexDirection="column" justifyContent="center">
            <ContactActionsDropdown contactId={params.id as string} />
            <ShowIfDebugWrapper debugKey="forms">
              <OpenInCuro curoType="contact" curoId={data.curoId}>
                Open contact in Curo
              </OpenInCuro>
            </ShowIfDebugWrapper>
          </Box>
        );
      },
    },
  ];
};

export const contactSPPColumns: ContactColumnProps[] = [
  {
    field: 'name',
    headerName: ' ',
    flex: 2,
    renderCell: RenderContactNameAndEmail,
  },

  {
    field: 'clientPortalStatus',
    headerName: 'Client Portal',
    flex: 1,
    editable: false,
    sortable: false,
    renderCell: (params) => {
      const data = params.row;
      if (!data?.statuses?.clientPortal?.status) {
        return RenderColorBasic({Loading: 'lightgrey'}, 'Loading', 'Loading');
      }
      return RenderColorBasic(
        clientPortalStatusCategoryColors,
        data.statuses?.clientPortal.status,
        data.statuses?.clientPortal.status === ClientPortalStatusCategory.not_started
          ? clientPortalStatusCategoryText.not_started
          : `${
              clientPortalStatusCategoryText[data.statuses.clientPortal.status]
            } ${newUTCDate(data.statuses?.clientPortal.lastUpdated).toLocaleDateString()}`
      );
    },
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    align: 'center',
    flex: 1,
    renderCell: renderPhone,
  },
  {
    field: 'email',
    headerName: 'Email',
    align: 'center',
    flex: 1,
    hideSortIcons: true,
    renderCell: renderEmail,
  },
];

export const ContactsGridItems: React.FC<object> = () => {
  const navigate = useNavigate();
  const {account} = useAccountContext();
  const {contacts} = useContext(ContactActionsContext);
  const {perms} = useDjangoContext();
  const contactSaltusColumns = useContactSaltusColumns();

  const rows = useMemo(() => {
    return sortAccountContacts(account?.id, contacts).map((contact) => ({
      ...contact,
      primary: contact.accountContacts.some((accountContact) => accountContact.isPrimary),
      secondary: contact.accountContacts.some(
        (accountContact) =>
          accountContact.isSecondary !== null && accountContact.isSecondary
      ),
      dependant: contact.contactStatus === 'Dependant',
    }));
  }, [contacts, account]);

  return (
    <Grid
      className="account-contacts-grid"
      item
      xs={12}
      mt={2}
      key={JSON.stringify(contacts?.[0])}
    >
      <SearchResults
        dataGridId="AccountContactGridItems"
        title="Contacts"
        autoHeight={false}
        search=""
        data={rows}
        columns={contactSaltusColumns}
        columnVisibilityModel={{
          atrSent: perms.includes(GlobalPermissionEnum.can_access_atrs),
          atrStatus: perms.includes(GlobalPermissionEnum.can_access_atrs),
          factFindStatus: perms.includes(GlobalPermissionEnum.can_access_cqs),
          fpaStatus: perms.includes(GlobalPermissionEnum.can_access_fpa),
        }}
        minHeight={260}
        hideFooter
        onRowDoubleClick={(params) => {
          navigate({
            pathname: `/contacts/${params.row.curoId}`,
          });
        }}
      />
    </Grid>
  );
};
